/**
 * BhiPreviewCard.jsx — iter902 (Feb 2026)
 *
 * Compact BHI snapshot preview surfaced on Dashboard + Profile so
 * users see their Breast Health Intelligence signal without navigating
 * to the full page. Pulls `/api/breast-intelligence/snapshot`.
 *
 * Shows:
 *   • Metabolic composite score
 *   • Meals / NIS / DIL / BMI mini-stats
 *   • Active ecosystem nodes count (how many tissue zones are lit)
 *   • Conditions count + last generated timestamp
 *   • CTA to open full BHI + Phenotype Explorer
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { HeartPulse, Sparkles, GitBranch, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";

const bandColor = (band) => {
  if (band === "protective") return "text-emerald-500";
  if (band === "attention")  return "text-amber-500";
  if (band === "concern")    return "text-rose-500";
  return "text-slate-400";
};
const bandLabel = (band) => ({
  protective: "Протективен",
  attention:  "Внимание",
  concern:    "Загриженост",
}[band] || "—");

export default function BhiPreviewCard({ compact = false, testid = "bhi-preview-card" }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/breast-intelligence/snapshot`)
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [API]);

  const cardCls = isDark
    ? "border-pink-500/30 bg-gradient-to-br from-pink-500/10 to-violet-500/10"
    : "border-pink-200 bg-gradient-to-br from-pink-50 to-violet-50";

  if (loading) {
    return (
      <div className={`rounded-2xl border p-4 ${cardCls}`} data-testid={testid}>
        <div className="flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm opacity-70">Зарежда BHI…</span>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={`rounded-2xl border p-4 ${cardCls}`} data-testid={testid}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <HeartPulse className="w-5 h-5 text-pink-500" />
            <div>
              <h3 className="text-sm font-bold">Здраве на гърдата</h3>
              <p className="text-xs opacity-70">Няма налични данни. Отвори модула, за да качиш лаборатория или епикриза.</p>
            </div>
          </div>
          <button
            onClick={() => navigate("/health-intelligence/breast")}
            data-testid={`${testid}-open`}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-gradient-to-r from-pink-500 to-violet-600 text-white inline-flex items-center gap-1"
          >
            Отвори <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    );
  }

  const metabolic = data.metabolic || {};
  const signals   = data.signals || {};
  const ecosystem = data.ecosystem || {};
  const conditions = Array.isArray(data.conditions) ? data.conditions : [];
  const meas = metabolic.measured_components || 0;
  const total = metabolic.total_components || 0;

  return (
    <div className={`rounded-2xl border p-4 ${cardCls}`} data-testid={testid}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-pink-500 to-violet-600 grid place-items-center">
            <HeartPulse className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-bold">Здраве на гърдата</h3>
            <div className="text-[11px] opacity-60">
              Последно ъпдейт: {data.generated_at ? new Date(data.generated_at).toLocaleDateString("bg") : "—"}
            </div>
          </div>
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => navigate("/health-intelligence/breast")}
            data-testid={`${testid}-open-bhi`}
            className="text-[11px] font-semibold px-2.5 py-1 rounded-lg bg-white/60 dark:bg-white/10 hover:bg-white dark:hover:bg-white/20 inline-flex items-center gap-1"
          >
            BHI <ArrowRight className="w-3 h-3" />
          </button>
          <button
            onClick={() => navigate("/phenotype-explorer")}
            data-testid={`${testid}-open-phenotype`}
            className="text-[11px] font-semibold px-2.5 py-1 rounded-lg bg-white/60 dark:bg-white/10 hover:bg-white dark:hover:bg-white/20 inline-flex items-center gap-1"
          >
            <GitBranch className="w-3 h-3" /> Explorer
          </button>
        </div>
      </div>

      {/* Metabolic score + band */}
      <div className="flex items-baseline gap-3 mb-3">
        <div>
          <div className={`text-3xl font-extrabold ${bandColor(metabolic.band)}`}>
            {metabolic.total != null ? `${metabolic.total}` : "—"}
            <span className="text-lg opacity-60 font-medium">/100</span>
          </div>
          <div className="text-[11px] opacity-60">Метаболитен BHI — {bandLabel(metabolic.band)}</div>
        </div>
        <div className={`text-[10px] px-2 py-0.5 rounded-full ${
          isDark ? "bg-white/10 text-slate-300" : "bg-white/70 text-slate-700"
        }`}>{meas}/{total} компонента</div>
      </div>

      {/* Mini stats grid */}
      {!compact && (
        <div className="grid grid-cols-4 gap-1.5 mb-3">
          <MiniStat label="NIS" value={signals.nis_avg || "—"} />
          <MiniStat label="DIL" value={signals.dil_avg || "—"} />
          <MiniStat label="BMI" value={signals.bmi || "—"} />
          <MiniStat label="Ястия/14д" value={signals.meals_last_14d || 0} />
        </div>
      )}

      {/* Ecosystem + conditions */}
      <div className={`text-xs rounded-lg p-2 ${isDark ? "bg-white/5" : "bg-white/60"}`}>
        <div className="flex items-center justify-between">
          <span className="opacity-70">Активни физиологични зони:</span>
          <b>{ecosystem.active_count || 0}/{(ecosystem.nodes || []).length || 0}</b>
        </div>
        {conditions.length > 0 && (
          <div className="flex items-center justify-between mt-1">
            <span className="opacity-70">Регистрирани състояния:</span>
            <b>{conditions.length}</b>
          </div>
        )}
      </div>

      {conditions.length > 0 && !compact && (
        <div className="flex flex-wrap gap-1 mt-2">
          {conditions.slice(0, 4).map((c, i) => (
            <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded ${
              isDark ? "bg-fuchsia-500/20 text-fuchsia-200" : "bg-fuchsia-100 text-fuchsia-700"
            }`}>
              {c.name_bg || c.condition_id || c}
            </span>
          ))}
          {conditions.length > 4 && (
            <span className="text-[10px] opacity-60 self-center">+{conditions.length - 4}</span>
          )}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-lg bg-white/40 dark:bg-white/5 p-1.5 text-center">
      <div className="text-[9px] uppercase tracking-wider opacity-60">{label}</div>
      <div className="text-sm font-bold">{value}</div>
    </div>
  );
}
